import React, { useCallback, useEffect, useState } from "react"
import toast from "react-hot-toast"
import { useNavigate } from "react-router-dom"
import {
  FaBan,
  FaBoxOpen,
  FaCheckCircle,
  FaFileInvoiceDollar,
  FaPaperPlane,
  FaPlus,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"
import { useAuth } from "../../provider/auth.provider"
import { useConfirm } from "../../provider/confirm.provider"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import {
  apiError,
  dateText,
  FilterOption,
  fetchPaged,
  fullName,
  materialFilters,
  materialMasters,
  money,
  qty,
  SupplierKind,
  supplierDisplayName,
} from "./shared/material.api"
import {
  AsyncPicker,
  Cell,
  DataTable,
  DetailRow,
  ExpandAction,
  Field,
  FilterField,
  FilterRow,
  GhostButton,
  IconAction,
  ListCard,
  OffCanvas,
  PrimaryButton,
  RefreshButton,
  ScopeNote,
  SearchBox,
  Select,
  StatusBadge,
  StatusTabs,
  SupplierPartyPicker,
  Td,
  TextArea,
  TextInput,
  Th,
} from "./shared/MaterialUI"

/**
 * Material Request Note (MRN) - the purchase request.
 *
 * Two kinds, and the difference matters:
 *   Warehouse purchase  the company buys and it lands in a warehouse.
 *   Staff purchase      someone buys near the site and it lands in their name.
 *
 * Approving an MRN commits nothing and moves nothing. The money is spent on
 * the arrangement (MAP) that follows it, and the stock only appears when that
 * arrangement is received.
 */

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "requested", label: "Pending Approval" },
  { key: "approved", label: "Approved" },
  { key: "arranged", label: "Arranged" },
  { key: "completed", label: "Completed" },
  { key: "rejected,cancelled", label: "Closed" },
]

interface LineDraft {
  key: string
  item: FilterOption | null
  quantity: string
  estimated_rate: string
  remarks: string
}

const blankLine = (): LineDraft => ({
  key: Math.random().toString(36).slice(2),
  item: null,
  quantity: "",
  estimated_rate: "",
  remarks: "",
})

const MaterialRequestsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { prompt } = useConfirm()
  const navigate = useNavigate()
  const canCreate = hasPermission("MaterialRequest", "create")
  const canApprove = hasPermission("MaterialRequest", "approve")
  const canCancel = hasPermission("MaterialRequest", "cancel")
  const canArrange = hasPermission("MaterialArrangement", "create")

  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [status, setStatus] = useState("")
  const [requestType, setRequestType] = useState("")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 400)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [showForm, setShowForm] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchPaged("/materials/requests", {
        page,
        limit,
        status: status || undefined,
        request_type: requestType || undefined,
        project_id: project?.id,
        search: debouncedSearch.trim() || undefined,
      })
      setRows(result.rows)
      setPagination(result.pagination)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load material requests"))
    } finally {
      setLoading(false)
    }
  }, [page, limit, status, requestType, project, debouncedSearch])

  useEffect(() => {
    load()
  }, [load])

  const act = async (id: number, action: "approve" | "reject" | "cancel" | "submit") => {
    let remarks: string | undefined
    if (action === "reject" || action === "cancel") {
      // The server requires a reason on both, so collect it in the same step
      // rather than confirming and then failing the call.
      const entered = await prompt({
        title: action === "reject" ? "Reject material request" : "Cancel material request",
        message:
          action === "reject"
            ? "The raiser will see your reason and can raise a fresh request."
            : "Withdrawing a request cannot be undone.",
        label: "Reason",
        placeholder: action === "reject" ? "Why is this being rejected?" : "Why is this being withdrawn?",
        required: true,
        multiline: true,
        confirmText: action === "reject" ? "Reject" : "Cancel request",
        cancelText: "Go back",
        variant: "warning",
      })
      if (entered === null) return
      remarks = entered
    }
    setBusyId(id)
    try {
      const res = await api.post(`/materials/requests/${id}/${action}`, remarks ? { remarks } : {})
      toast.success(res.data?.message || "Done")
      load()
    } catch (e: any) {
      toast.error(apiError(e, `Failed to ${action} the request`))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <ListCard
        icon={<FaBoxOpen className="w-5 h-5" />}
        title="Material Requests (MRN)"
        subtitle="Requests to procure material - a warehouse purchase, or a staff purchase near the site. Approving one commits no money and moves no stock."
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            {canCreate ? (
              <PrimaryButton onClick={() => setShowForm(true)}>
                <FaPlus className="w-4 h-4" /> Raise Request
              </PrimaryButton>
            ) : null}
          </>
        }
        filters={
          <>
            <FilterRow>
              <FilterField label="Search" grow>
                <SearchBox
                  value={search}
                  onChange={(v) => {
                    setSearch(v)
                    setPage(1)
                  }}
                  placeholder="Request number, site, supplier..."
                />
              </FilterField>
              <FilterField label="Project">
                <AsyncPicker
                  value={project}
                  onChange={(o) => {
                    setProject(o)
                    setPage(1)
                  }}
                  fetcher={(s) => materialFilters.projects(s)}
                  placeholder="All my projects"
                  emptyHint="You are not assigned to any project"
                />
              </FilterField>
              <FilterField label="Request Type">
                <Select
                  value={requestType}
                  onChange={(e) => {
                    setRequestType(e.target.value)
                    setPage(1)
                  }}
                >
                  <option value="">All request types</option>
                  <option value="warehouse_purchase">Warehouse purchase</option>
                  <option value="staff_purchase">Staff purchase</option>
                </Select>
              </FilterField>
            </FilterRow>
            <StatusTabs
              tabs={STATUS_TABS}
              value={status}
              onChange={(k) => {
                setStatus(k)
                setPage(1)
              }}
            />
          </>
        }
        footer={
          pagination ? (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          ) : null
        }
      >
        <DataTable
          colSpan={9}
          loading={loading}
          empty={rows.length === 0}
          loadingText="Loading material requests..."
          emptyText="No material requests found"
          emptyHint={<ScopeNote />}
          emptyIcon={<FaBoxOpen />}
          head={
            <>
              <Th>Actions</Th>
              <Th>Request</Th>
              <Th>Type</Th>
              <Th>Project / Site</Th>
              <Th>Lines</Th>
              <Th>Est. Amount</Th>
              <Th>Raised By</Th>
              <Th>Required By</Th>
              <Th>Status</Th>
            </>
          }
        >
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="hover:bg-gray-50 transition-colors">
                <Cell>
                  <div className="flex items-center gap-1">
                    <ExpandAction
                      open={expanded === row.id}
                      onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                      what="items"
                    />
                    {row.status === "draft" && canCreate ? (
                      <IconAction title="Submit for approval" disabled={busyId === row.id} onClick={() => act(row.id, "submit")}>
                        <FaPaperPlane />
                      </IconAction>
                    ) : null}
                    {row.status === "requested" && canApprove ? (
                      <>
                        <IconAction title="Approve" tone="green" disabled={busyId === row.id} onClick={() => act(row.id, "approve")}>
                          <FaCheckCircle />
                        </IconAction>
                        <IconAction title="Reject" tone="red" disabled={busyId === row.id} onClick={() => act(row.id, "reject")}>
                          <FaTimesCircle />
                        </IconAction>
                      </>
                    ) : null}
                    {(row.status === "approved" || row.status === "arranged") && canArrange ? (
                      <IconAction
                        title="Arrange (raise a MAP)"
                        tone="indigo"
                        onClick={() => navigate(`/materials/arrangements?request_id=${row.id}`)}
                      >
                        <FaFileInvoiceDollar />
                      </IconAction>
                    ) : null}
                    {["draft", "requested", "approved"].includes(row.status) && canCancel ? (
                      <IconAction title="Cancel request" tone="gray" disabled={busyId === row.id} onClick={() => act(row.id, "cancel")}>
                        <FaBan />
                      </IconAction>
                    ) : null}
                  </div>
                </Cell>
                <Cell className="font-medium">{row.request_number}</Cell>
                <Cell>
                  {row.request_type === "staff_purchase" ? "Staff purchase" : "Warehouse purchase"}
                  {row.request_type === "staff_purchase" && row.purchaser ? (
                    <div className="text-xs text-gray-500">{fullName(row.purchaser)}</div>
                  ) : null}
                </Cell>
                <Cell>
                  <div>{row.project?.project_short_name || row.project?.project_name || "-"}</div>
                  <div className="text-xs text-gray-500">{row.inc_site?.site_name || row.site_name || "-"}</div>
                </Cell>
                <Cell>{(row.items || []).length}</Cell>
                <Cell>{row.estimated_amount ? money(row.estimated_amount) : "-"}</Cell>
                <Cell>{fullName(row.requester)}</Cell>
                <Cell>{dateText(row.required_by_date)}</Cell>
                <Cell>
                  <StatusBadge status={row.status} />
                </Cell>
              </tr>
              {expanded === row.id ? (
                <DetailRow colSpan={9}>
                  <RequestDetail row={row} />
                </DetailRow>
              ) : null}
            </React.Fragment>
          ))}
        </DataTable>
      </ListCard>

      {showForm ? (
        <RequestForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false)
            setPage(1)
            load()
          }}
        />
      ) : null}
    </div>
  )
}

const RequestDetail: React.FC<{ row: any }> = ({ row }) => (
  <div className="space-y-3 py-2">
    <table className="min-w-full text-sm bg-white border border-gray-200 rounded">
      <thead className="bg-gray-100">
        <tr>
          <Th>Item</Th>
          <Th>Requested</Th>
          <Th>Approved</Th>
          <Th>Arranged</Th>
          <Th>Received</Th>
          <Th>Est. Rate</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {(row.items || []).map((line: any) => (
          <tr key={line.id}>
            <Td>
              {line.item?.item_name || `#${line.item_id}`}
              {line.item?.item_code ? <span className="text-xs text-gray-400"> ({line.item.item_code})</span> : null}
            </Td>
            <Td>
              {qty(line.requested_quantity)} {line.unit?.short_name || ""}
            </Td>
            <Td>{line.approved_quantity === null ? "-" : qty(line.approved_quantity)}</Td>
            <Td>{qty(line.arranged_quantity)}</Td>
            <Td>{qty(line.received_quantity)}</Td>
            <Td>{line.estimated_rate ? money(line.estimated_rate) : "-"}</Td>
            <Td className="text-gray-500">{line.remarks || "-"}</Td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-600">
      <div>
        <span className="font-medium text-gray-800">Supplier:</span> {supplierDisplayName(row)}
      </div>
      <div>
        <span className="font-medium text-gray-800">Delivery:</span> {row.delivery_location || "-"}
      </div>
      <div>
        <span className="font-medium text-gray-800">Delivery remarks:</span> {row.delivery_remarks || "-"}
      </div>
      <div>
        <span className="font-medium text-gray-800">Site remarks:</span> {row.site_remarks || "-"}
      </div>
      {row.approver_remarks ? (
        <div className="md:col-span-2">
          <span className="font-medium text-gray-800">Approver:</span> {row.approver_remarks}
        </div>
      ) : null}
      {row.rejection_remarks ? (
        <div className="md:col-span-2 text-red-700">
          <span className="font-medium">Rejected:</span> {row.rejection_remarks}
        </div>
      ) : null}
    </div>
  </div>
)

const RequestForm: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [saving, setSaving] = useState(false)
  const [requestType, setRequestType] = useState<"warehouse_purchase" | "staff_purchase">("warehouse_purchase")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [site, setSite] = useState<FilterOption | null>(null)
  const [warehouse, setWarehouse] = useState<FilterOption | null>(null)
  const [purchaser, setPurchaser] = useState<FilterOption | null>(null)
  const [supplier, setSupplier] = useState<FilterOption | null>(null)
  const [supplierKind, setSupplierKind] = useState<SupplierKind>("supplier")
  const [form, setForm] = useState<Record<string, string>>({
    required_by_date: "",
    delivery_location: "",
    delivery_remarks: "",
    site_remarks: "",
    remarks: "",
    estimated_amount: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    upi_id: "",
  })
  const [lines, setLines] = useState<LineDraft[]>([blankLine()])
  const [files, setFiles] = useState<FileList | null>(null)

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }))

  const submit = async (submitForApproval: boolean) => {
    if (!project) return toast.error("Pick a project")
    const validLines = lines.filter((l) => l.item && Number(l.quantity) > 0)
    if (!validLines.length) return toast.error("Add at least one item line with a quantity")
    if (requestType === "warehouse_purchase" && !warehouse) {
      return toast.error("Pick the warehouse the material should land in")
    }

    const payload = new FormData()
    payload.append("project_id", String(project.id))
    payload.append("request_type", requestType)
    payload.append("submit", String(submitForApproval))
    if (site) payload.append("inc_site_id", String(site.id))
    if (warehouse) payload.append("warehouse_id", String(warehouse.id))
    if (purchaser) payload.append("purchaser_user_id", String(purchaser.id))
    if (supplier) payload.append(supplierKind === "vendor" ? "vendor_id" : "supplier_id", String(supplier.id))
    Object.entries(form).forEach(([key, value]) => {
      if (value) payload.append(key, value)
    })
    // FormData has no nested arrays, so the lines travel as JSON and the
    // server parses them out of the body alongside the files.
    payload.append(
      "items",
      JSON.stringify(
        validLines.map((l) => ({
          item_id: l.item!.id,
          requested_quantity: Number(l.quantity),
          estimated_rate: l.estimated_rate ? Number(l.estimated_rate) : null,
          remarks: l.remarks || null,
        })),
      ),
    )
    if (files) Array.from(files).forEach((f) => payload.append("documents", f))

    setSaving(true)
    try {
      const res = await api.post("/materials/requests", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      toast.success(res.data?.message || "Material request raised")
      onSaved()
    } catch (e: any) {
      toast.error(apiError(e, "Failed to raise the request"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <OffCanvas
      onClose={onClose}
      title="Raise a material request"
      subtitle="A warehouse purchase lands in a warehouse. A staff purchase lands in the buyer's own name and is issued from there."
      width="xl"
      footer={
        <>
          <GhostButton onClick={() => submit(false)} disabled={saving}>
            Save as draft
          </GhostButton>
          <PrimaryButton loading={saving} onClick={() => submit(true)}>
            Submit for approval
          </PrimaryButton>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Request type" required>
          <Select value={requestType} onChange={(e) => setRequestType(e.target.value as any)}>
            <option value="warehouse_purchase">Warehouse purchase</option>
            <option value="staff_purchase">Staff purchase</option>
          </Select>
        </Field>
        <Field label="Project" required hint="Only the projects you are assigned to">
          <AsyncPicker
            value={project}
            onChange={setProject}
            fetcher={(s) => materialFilters.projects(s)}
            placeholder="Select project..."
            emptyHint="You are not assigned to any project"
          />
        </Field>
        <Field label="Site" hint="Optional - for material tied to one site">
          <AsyncPicker
            value={site}
            onChange={setSite}
            fetcher={(s) => materialFilters.sites(project ? String(project.id) : undefined, s)}
            dependency={project?.id}
            disabled={!project}
            placeholder="Select site..."
          />
        </Field>

        {requestType === "warehouse_purchase" ? (
          <Field label="Deliver into warehouse" required>
            <AsyncPicker
              value={warehouse}
              onChange={setWarehouse}
              fetcher={(s) => materialMasters.warehouses(s)}
              placeholder="Select warehouse..."
            />
          </Field>
        ) : (
          <Field label="Purchased by" hint="Defaults to you. The material is issued to this person's name.">
            <AsyncPicker
              value={purchaser}
              onChange={setPurchaser}
              fetcher={(s) => materialFilters.staff(project ? String(project.id) : undefined, s)}
              dependency={project?.id}
              placeholder="Defaults to you"
            />
          </Field>
        )}

        <Field label="Required by">
          <TextInput
            type="date"
            value={form.required_by_date}
            onChange={(e) => set("required_by_date", e.target.value)}
          />
        </Field>
        <Field label="Indicative amount" hint="The negotiated figure is captured on the arrangement">
          <TextInput
            type="number"
            min="0"
            step="0.01"
            value={form.estimated_amount}
            onChange={(e) => set("estimated_amount", e.target.value)}
          />
        </Field>

        <Field label="Supplier" hint="Optional at this stage. A vendor from the Vendor master can also be the supplier">
          <SupplierPartyPicker kind={supplierKind} onKindChange={setSupplierKind} value={supplier} onChange={setSupplier} />
        </Field>
        <Field label="Delivery location">
          <TextInput value={form.delivery_location} onChange={(e) => set("delivery_location", e.target.value)} />
        </Field>
        <Field label="Delivery remarks">
          <TextInput value={form.delivery_remarks} onChange={(e) => set("delivery_remarks", e.target.value)} />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Field label="Bank name">
          <TextInput value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} />
        </Field>
        <Field label="Account number">
          <TextInput value={form.account_number} onChange={(e) => set("account_number", e.target.value)} />
        </Field>
        <Field label="IFSC">
          <TextInput value={form.ifsc_code} onChange={(e) => set("ifsc_code", e.target.value)} />
        </Field>
        <Field label="UPI id">
          <TextInput value={form.upi_id} onChange={(e) => set("upi_id", e.target.value)} />
        </Field>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Items</h3>
          <GhostButton onClick={() => setLines((l) => [...l, blankLine()])}>
            <FaPlus /> Add line
          </GhostButton>
        </div>
        <div className="border border-gray-200 rounded overflow-visible">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th className="w-2/5">Item</Th>
                <Th>Quantity</Th>
                <Th>Est. rate</Th>
                <Th>Remarks</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((line, index) => (
                <tr key={line.key}>
                  <Td>
                    <AsyncPicker
                      value={line.item}
                      onChange={(o) =>
                        setLines((ls) => ls.map((l, i) => (i === index ? { ...l, item: o } : l)))
                      }
                      fetcher={(s) => materialMasters.items(s)}
                      placeholder="Select item..."
                    />
                  </Td>
                  <Td>
                    <TextInput
                      type="number"
                      min="0"
                      step="0.001"
                      value={line.quantity}
                      onChange={(e) =>
                        setLines((ls) => ls.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l)))
                      }
                    />
                  </Td>
                  <Td>
                    <TextInput
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.estimated_rate}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((l, i) => (i === index ? { ...l, estimated_rate: e.target.value } : l)),
                        )
                      }
                    />
                  </Td>
                  <Td>
                    <TextInput
                      value={line.remarks}
                      onChange={(e) =>
                        setLines((ls) => ls.map((l, i) => (i === index ? { ...l, remarks: e.target.value } : l)))
                      }
                    />
                  </Td>
                  <Td>
                    <button
                      onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== index) : ls))}
                      className="text-red-500 hover:text-red-700 disabled:opacity-30"
                      disabled={lines.length === 1}
                    >
                      <FaTrash />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Site remarks">
          <TextArea rows={2} value={form.site_remarks} onChange={(e) => set("site_remarks", e.target.value)} />
        </Field>
        <Field label="Remarks">
          <TextArea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
        </Field>
      </div>

      <Field label="Documents" hint="Quotes, proforma invoices, a payment QR code">
        <input
          type="file"
          multiple
          onChange={(e) => setFiles(e.target.files)}
          className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:bg-gray-100 file:text-gray-700"
        />
      </Field>
    </OffCanvas>
  )
}

export default MaterialRequestsPage
